import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { router } from '@inertiajs/react'
import { CheckCircle, XCircle, Image as ImageIcon } from 'lucide-react'
import { useState } from 'react'

interface Submission {
  id: number
  user_name: string
  user_id: number
  image_url: string
  image_thumb_url: string
  caption: string
  votes_count: number
  status: string
  status_label: string
  rank: number | null
  created_at: string
}

interface Contest {
  id: number
  title: string
  status: string
}

interface IndexProps {
  contest: Contest
  submissions: Submission[]
}

export default function Index({ contest, submissions }: IndexProps) {
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null)

  const handleApprove = (submission: Submission) => {
    if (confirm(`Approuver la soumission de ${submission.user_name} ?`)) {
      router.post(`/admin/photo_contests/${contest.id}/submissions/${submission.id}/approve`)
    }
  }

  const handleReject = (submission: Submission) => {
    const reason = prompt('Raison du rejet:')
    if (reason) {
      router.post(`/admin/photo_contests/${contest.id}/submissions/${submission.id}/reject`, {
        rejection_reason: reason
      })
    }
  }

  return (
    <AdminLayout
      title="Modération Soumissions"
      breadcrumbs={[
        { label: 'Concours Photo', href: '/admin/photo_contests' },
        { label: contest.title, href: `/admin/photo_contests/${contest.id}` },
        { label: 'Soumissions' }
      ]}
    >
      <div className="mb-6">
        <p className="text-text-gray">{submissions.length} soumission(s)</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {submissions.map((submission) => (
          <Card key={submission.id} className="overflow-hidden">
            {/* Image */}
            <div
              className="aspect-square bg-gray-100 cursor-pointer"
              onClick={() => setSelectedSubmission(submission)}
            >
              {submission.image_url ? (
                <img
                  src={submission.image_url}
                  alt={submission.caption || 'Photo'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon size={48} className="text-gray-300" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-text-dark">{submission.user_name}</p>
                <Badge className={
                  submission.status === 'approved' ? 'bg-green-100 text-green-800' :
                  submission.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-orange-100 text-orange-800'
                }>
                  {submission.status_label}
                </Badge>
              </div>

              {submission.caption && (
                <p className="text-sm text-text-gray mb-3 line-clamp-2">{submission.caption}</p>
              )}

              <div className="flex items-center justify-between text-sm text-text-gray mb-3">
                <span>{submission.votes_count} vote(s)</span>
                {submission.rank && (
                  <Badge className="bg-yellow-100 text-yellow-800">
                    {submission.rank === 1 ? '🥇' : submission.rank === 2 ? '🥈' : '🥉'}
                  </Badge>
                )}
              </div>

              {/* Actions */}
              {submission.status === 'pending_review' && (
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleApprove(submission)}
                    className="flex-1 bg-green-600 hover:bg-green-700 flex items-center justify-center gap-1"
                  >
                    <CheckCircle size={16} />
                    Approuver
                  </Button>
                  <Button
                    onClick={() => handleReject(submission)}
                    variant="secondary"
                    className="flex-1 text-red-600 hover:bg-red-50 flex items-center justify-center gap-1"
                  >
                    <XCircle size={16} />
                    Rejeter
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Modal Preview */}
      {selectedSubmission && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedSubmission(null)}
        >
          <div className="max-w-4xl max-h-[90vh] relative">
            <img
              src={selectedSubmission.image_url}
              alt={selectedSubmission.caption || 'Photo'}
              className="max-w-full max-h-[90vh] object-contain"
            />
            {selectedSubmission.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-4">
                <p>{selectedSubmission.caption}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
